import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { DEMARRAGE_KEY } from '@teetsh/app/src/containers/Demarrage/queries/key';
import {
  DemarrageData,
  UserIntent,
  UserRole,
} from '@teetsh/app/src/containers/Demarrage/types';
import { ClassroomsOrg } from '@teetsh/app/src/types/school';
import { useTranslation } from 'react-i18next';
import { Role } from '@teetsh/app/src/types/user';
import { USER_KEY } from '@teetsh/app/src/queries/user/useUser';
import { ScholarSystemValue } from 'containers/scholarSystem';

interface SeachQuery {
  query: string;
}

interface Payload {
  userRoles?: UserRole[];
  roles?: Role[];
  otherRoleDetails?: string;
  edtTemplateId?: string;
  sequenceTemplateId?: string;
  programTemplateId?: string;
  edtTemplateDocumentId?: string;
  sequenceTemplateDocumentId?: string;
  programTemplateDocumentId?: string;
  etablissementUai?: string;
  etablissementNcesSchoolId?: string;
  etablissementACARASchoolId?: string;
  schoolName?: string;
  zoneId?: string;
  classroomsOrg?: ClassroomsOrg;
  userIntents?: UserIntent[];
  userCountryCode?: string;
  scholarSystem?: ScholarSystemValue;
  searchQueries?: SeachQuery[];
  degreesWithWorkingDays?: Array<{
    degree: string;
    workingDays: number[];
  }>;
}

export function useUpdateDemarrage() {
  const { notifyError } = useNotification();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['onboarding']);
  const currentOnboardingData: DemarrageData | undefined =
    queryClient.getQueryData([DEMARRAGE_KEY]);
  const requestURL = '/user/onboarding';
  const { mutateAsync } = useMutation({
    mutationFn: (payload: Payload) =>
      request(`${requestURL}`, {
        method: 'POST',
        body: {
          ...(currentOnboardingData || {}),
          ...payload,
          userRole: undefined, // unset userRole to avoid conflict with userRoles : this could be removed after full deployment
        },
      }),
    onMutate: payload => {
      queryClient.setQueryData<DemarrageData | undefined>(
        [DEMARRAGE_KEY],
        old => {
          if (!old) {
            return old;
          }
          return {
            ...(old || {}),
            ...(payload as DemarrageData),
          };
        },
      );
    },
    onSuccess: async (data, payload) => {
      if (payload.roles) {
        await queryClient.refetchQueries({
          queryKey: [USER_KEY],
        });
      }
    },
    onError: () => {
      notifyError({
        title: t('onboarding:messages.finalizeError'),
      });
    },
  });
  return mutateAsync;
}
